import { motion } from "framer-motion";
import { Check, Minus, ArrowUpRight } from "lucide-react";

const standard = [
  "A beautiful resort stay",
  "Standard reservation",
  "Standard dining access",
  "You coordinate the details",
  "You research upgrades",
  "You arrange special occasions",
  "Standard resort experience",
];

const elevated = [
  "Luxury resort stay",
  "Dedicated concierge",
  "Personal butler where available",
  "Priority dining",
  "Reserved sunbeds · exclusive beach access",
  "Nightly turndown gifts",
  "Spa privileges · golf savings",
  "Special occasion experiences",
  "VIP amenities · personalized curation",
];

export default function Comparison() {
  return (
    <section id="why" className="relative bg-[#F9F6F0] py-24 md:py-40 z-[2]" data-testid="comparison-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.9 }}
          className="grid grid-cols-12 gap-6 mb-16 md:mb-20"
        >
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A059]" />
              <span className="eyebrow text-[#C5A059]">Why Book With Us</span>
            </div>
            <h2 className="font-serif-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              You could book a resort.<br />
              <span className="italic text-[#C5A059]">Or you could elevate</span> the entire experience.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex md:items-end">
            <p className="text-[#1A1A1A]/75 leading-relaxed max-w-md">
              Same resort. Same beach. Same suite category. Entirely different arrival.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
          {/* Booking on your own */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="relative border border-black/10 bg-[#F2ECE0]/50 p-8 md:p-12"
            data-testid="comparison-standard"
          >
            <p className="eyebrow text-[#5C5C5C] mb-6">Option A</p>
            <h3 className="font-serif-editorial text-3xl md:text-4xl mb-10 text-[#1A1A1A]/80">
              Booking on <span className="italic">your own</span>
            </h3>
            <ul className="space-y-4">
              {standard.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-[#1A1A1A]/70 leading-relaxed">
                  <Minus size={16} strokeWidth={1.5} className="mt-1 shrink-0 text-[#5C5C5C]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* The Elevated Escape */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="relative border border-[#C5A059]/50 bg-[#1A1A1A] text-[#F9F6F0] p-8 md:p-12 md:-mt-6 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.5)]"
            data-testid="comparison-elevated"
          >
            <p className="eyebrow text-[#C5A059] mb-6">Option B · The Elevated Escape</p>
            <h3 className="font-serif-editorial text-3xl md:text-4xl mb-10">
              Booking with <span className="italic text-[#E6C5C0]">us</span>
            </h3>
            <ul className="space-y-4">
              {elevated.map((s, i) => (
                <li key={i} className="flex items-start gap-3 leading-relaxed">
                  <Check size={16} strokeWidth={1.75} className="mt-1 shrink-0 text-[#C5A059]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-16 md:mt-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8"
        >
          <p className="font-serif-editorial text-2xl md:text-3xl italic text-[#1A1A1A] max-w-xl leading-tight">
            Same destination.<br />Entirely different experience.
          </p>
          <a href="#inquiry" data-testid="comparison-cta" className="ee-btn-primary self-start md:self-auto">
            Elevate My Trip <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
